import React, { PureComponent } from 'react';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { bool, func, any, string, number } from 'prop-types';
import numeral from 'numeral';
import Radium from 'radium';
import { required, lengthEquals, pattern } from '@widergy/web-utils/lib/validate';
import { scrollToInvalid } from '@widergy/web-utils/lib/form';
import { UTTextInput, UTButton, UTLabel, AlertHandler, WithLoading } from '@widergy/energy-ui';
import { getIn } from 'seamless-immutable';

import { appConfig } from 'config/customization';
import UTCheckbox from 'app/components/UTCheckbox';
import { message } from 'utils/stringUtils';
import { ACCOUNT_HOLDER } from 'constants/reduxForms';
import { normalizeAccount } from 'utils/formatUtils';
import UTForm from 'app/components/UTForm';

import AccountDebtTable from './components/AccountDebtTable';
import { accountDebtColumns } from './components/AccountDebtTable/constants';
import { accountHolderChangeFields, WARRANTY_CHARGE_POSESSIONS } from './constants';
import { getAccountDebtConcepts, getAccountDueConcepts, getTotalAmount } from './accountHolderChangeHelper';
import stylesJS from './styles';
import styles from './styles.module.scss';

class AccountHolderChange extends PureComponent {
  state = { termsChecked: false, disableSubmit: false };

  accountNumberValidations = [
    required(message('Accounts:accountRequired')),
    lengthEquals(
      appConfig.ACCOUNT_LENGTH,
      message('Accounts:lengthValidation', {
        count: appConfig.ACCOUNT_LENGTH
      })
    ),
    pattern(appConfig.clientNumberRegex, message('Validations:invalidFormat'))
  ];

  componentWillReceiveProps = nextProps => {
    if (nextProps.supplyDestination && nextProps.supplyDestination !== this.props.supplyDestination) {
      if (nextProps.supplyDestination === 'Comercio') {
        AlertHandler.info(message('AccountHolderChange:commercialHolderChangeError'));
        this.setState({ disableSubmit: true });
      } else if (nextProps.supplyDestination === 'Consorcio, obra y otros') {
        AlertHandler.info(message('AccountHolderChange:othersHolderChangeError'));
        this.setState({ disableSubmit: true });
      } else this.setState({ disableSubmit: false });
    }
  };

  getAccreditPossesionValue = () =>
    accountHolderChangeFields()
      .map(field => field.key === 'accreditPossession' && `id${field.id}`)
      .filter(item => item)
      .find(item => item);

  includesWarrantyCharge = () =>
    WARRANTY_CHARGE_POSESSIONS.indexOf(this.props.accreditPossession[this.getAccreditPossesionValue()]) >= 0;

  handleOnTermsCheck = () => this.setState(prevState => ({ termsChecked: !prevState.termsChecked }));

  render() {
    const { termsChecked, disableSubmit } = this.state;
    return (
      <div style={stylesJS.container}>
        <div style={stylesJS.titleContainer}>
          <UTLabel large extrabold style={stylesJS.title}>
            {message('AccountHolderChange:accountHolderChangeTitle')}
          </UTLabel>
          <UTLabel style={stylesJS.title}>
            {message('AccountHolderChange:accountHolderChangeSubtitle')}
          </UTLabel>
        </div>
        <div style={stylesJS.field}>
          <Field
            component={UTTextInput}
            name="accountNumber"
            disabled={!!this.props.clientNumber}
            label={message('AccountHolderChange:clientNumberInput', {
              accountLength: appConfig.ACCOUNT_LENGTH
            })}
            validate={this.accountNumberValidations}
            normalize={normalizeAccount}
          />
        </div>
        {!this.props.shouldShowAccountHolderChangeForm && (
          <UTButton
            disabled={this.props.submitFailed && this.props.invalid}
            className={styles.continueButton}
            onPress={this.props.handleSubmit}
          >
            {message('AccountHolderChange:continue')}
          </UTButton>
        )}

        {this.props.shouldShowAccountHolderChangeForm && (
          <div>
            <div className={styles.formContainer}>
              <UTForm fields={this.props.fields} fieldValues={this.props.fieldValues} replaceFieldNames />
            </div>

            <div style={stylesJS.checkContainer}>
              <UTCheckbox onChange={this.handleOnTermsCheck} value={termsChecked} />
              <UTLabel small>
                {message('RegisterAccount:checkLabel', { regulationURL: this.props.regulationURL })}
              </UTLabel>
            </div>

            <UTLabel small style={stylesJS.checkContainer}>
              {message('AccountHolderChange:userDataLabel')}
            </UTLabel>
            {this.includesWarrantyCharge() && (
              <div style={stylesJS.warranties}>
                <UTLabel extrabold style={{ ...stylesJS.title, ...stylesJS.checkContainer }}>
                  {message('AccountHolderChange:nextBillCharge')}
                </UTLabel>
                <UTLabel style={stylesJS.checkContainer}>
                  {message('AccountHolderChange:warrantyCharge', {
                    amount: numeral(this.props.warrantyCharge).format('$ 0.[00]')
                  })}
                </UTLabel>
              </div>
            )}

            <div style={stylesJS.table}>
              <div style={stylesJS.tableContainer}>
                <AccountDebtTable
                  title={message('AccountHolderChange:clientDebt', {
                    supply: this.props.clientNumber
                  })}
                  concepts={getAccountDebtConcepts(
                    this.props.accountTotalDebt && this.props.accountTotalDebt.Recibos
                  )}
                  tableColumns={accountDebtColumns}
                />
              </div>
              <div style={stylesJS.tableContainer}>
                <AccountDebtTable
                  title={message('AccountHolderChange:ownDebt', {
                    holder: this.props.currentAccountHolderName
                  })}
                  concepts={getAccountDueConcepts(
                    this.props.accountDueDebt && this.props.accountDueDebt.Deuda
                  )}
                  tableColumns={accountDebtColumns}
                />
              </div>

              <UTLabel extrabold style={stylesJS.totalDebt}>
                {message('AccountHolderChange:userDebt', {
                  debt: getTotalAmount(this.props.accountTotalDebt, this.props.accountDueDebt)
                })}
              </UTLabel>
            </div>
            <UTLabel style={stylesJS.userDebitCancel}>
              {message('AccountHolderChange:userDebtCancel')}
            </UTLabel>

            <UTButton
              className={styles.continueButton}
              onPress={this.props.handleSubmit}
              disabled={(!termsChecked && this.props.submitFailed && this.props.invalid) || disableSubmit}
            >
              {message('AccountHolderChange:createProcedure')}
            </UTButton>
          </div>
        )}
      </div>
    );
  }
}

AccountHolderChange.propTypes = {
  handleSubmit: func.isRequired,
  fieldValues: any, // eslint-disable-line react/forbid-prop-types
  shouldShowAccountHolderChangeForm: bool,
  accountTotalDebt: any, // eslint-disable-line
  accountDueDebt: any, // eslint-disable-line
  clientNumber: string,
  fields: any, // eslint-disable-line react/forbid-prop-types
  currentAccountHolderName: string,
  supplyDestination: string,
  accreditPossession: any, // eslint-disable-line react/forbid-prop-types
  submitFailed: bool,
  invalid: bool,
  warrantyCharge: number,
  regulationURL: string
};

const form = reduxForm({ form: ACCOUNT_HOLDER, onSubmitFail: errors => scrollToInvalid(errors) })(
  Radium(AccountHolderChange)
);
const formSelector = formValueSelector(ACCOUNT_HOLDER);

const mapStateToProps = store => ({
  fieldValues: formSelector(store, ...accountHolderChangeFields().map(field => `id${field.id}`)),
  regulationURL: getIn(store, ['utility', 'appConfiguration', 'supply_regulation_docs_url']),
  accreditPossession: formSelector(
    store,
    ...accountHolderChangeFields().map(field => field.key === 'accreditPossession' && `id${field.id}`)
  ),
  supplyDestination: formSelector(store, 'id2')
});

export default WithLoading(props => props.loading)(connect(mapStateToProps)(form));
